import React, { Suspense, useEffect } from "react";
import InternalToolsHome from "./internalTools/components/InternalToolsHome";
import { INTERNAL_TOOL_REGISTRY } from "./internalTools/config/toolRegistry";
import {
    canAccessInternalTool,
    captureAppSensitiveLaunchTokenFromLocation,
    createToolAccessContextFromLocation,
} from "./internalTools/access/toolAccess";
import "./internalTools/internalTools.css";

const TOOL_ROUTE_ALIASES = {
    "/email-generator": "email-address-generator",
    "/bas-calculator": "bas-calculator",
    "/construction-feasibility": "construction-feasibility",
    "/lease-wale-calculator": "lease-wale-calculator",
    "/servicing-calculator": "servicing-calculator",
};

const ToolLoadingFallback = () => (
    <div className="internal-tool-card__loading" role="status" aria-live="polite">
        Loading tool...
    </div>
);

function resolveRequestedToolKey() {
    if (typeof window === "undefined") {
        return "";
    }

    const searchParams = new URLSearchParams(window.location.search);
    const queryToolKey = searchParams.get("tool");

    if (queryToolKey) {
        return queryToolKey;
    }

    const pathname = window.location.pathname.replace(/\/+$/, "") || "/";

    return TOOL_ROUTE_ALIASES[pathname] || "";
}

const StandaloneToolView = ({ tool }) => {
    const ToolComponent = tool.Component;

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.title = `${tool.title} | Financial Edge Calculators`;
        }
    }, [tool.title]);

    const standaloneClassName = `app-shell app-shell--standalone-tool app-shell--${tool.key}`;
    const cardClassName = `internal-tool-card internal-tool-card--standalone internal-tool-card--${tool.key}`;

    return (
        <main className={standaloneClassName}>
            <div className="internal-tools-shell internal-tools-shell--standalone">
                <div className="internal-tools-topbar">
                    <a className="internal-tools-topbar__brand" href="/">
                        Financial Edge Calculators
                    </a>
                    <span className="internal-tools-topbar__meta">
                        {tool.standaloneEyebrow || "Hosted tool"}
                    </span>
                </div>

                <section className={cardClassName}>
                    <Suspense fallback={<ToolLoadingFallback />}>
                        <ToolComponent standalone />
                    </Suspense>
                </section>
            </div>
        </main>
    );
};

const StandaloneToolAccessDenied = ({ tool }) => {
    useEffect(() => {
        if (typeof document !== "undefined") {
            document.title = `${tool.title} unavailable`;
        }
    }, [tool.title]);

    return (
        <main className="app-shell app-shell--standalone-tool">
            <div className="internal-tools-shell internal-tools-shell--standalone">
                <header className="internal-tools-header internal-tools-header--standalone">
                    <div className="internal-tools-header__copy">
                        <p className="internal-tools-header__eyebrow">Tool unavailable</p>
                        <h1>{tool.title}</h1>
                        <p>This tool is not available in the current workspace.</p>
                    </div>
                </header>

                <section className="internal-tool-card internal-tool-card--standalone">
                    <div className="space-y-3 text-slate-900">
                        <h2 className="m-0 text-2xl font-semibold">Access restricted</h2>
                        <p className="m-0 max-w-2xl text-base leading-7 text-slate-600">
                            This tool is either reserved for a specific brokerage or excluded from the
                            current beta workspace profile.
                        </p>
                    </div>
                </section>
            </div>
        </main>
    );
};

const App = () => {
    const requestedToolKey = resolveRequestedToolKey();
    const accessContext = createToolAccessContextFromLocation();
    const requestedTool = INTERNAL_TOOL_REGISTRY.find((tool) => tool.key === requestedToolKey);
    const visibleTools = INTERNAL_TOOL_REGISTRY.filter((tool) =>
        canAccessInternalTool(tool, accessContext)
    );
    const homeTools = visibleTools.filter((tool) =>
        tool.placements?.includes("public-tool-gallery")
    );

    useEffect(() => {
        captureAppSensitiveLaunchTokenFromLocation();
    }, []);

    useEffect(() => {
        if (!requestedTool && typeof document !== "undefined") {
            document.title = "Financial Edge Calculators";
        }
    }, [requestedTool]);

    if (requestedTool) {
        if (!canAccessInternalTool(requestedTool, accessContext)) {
            return <StandaloneToolAccessDenied tool={requestedTool} />;
        }

        return <StandaloneToolView tool={requestedTool} />;
    }

    return <InternalToolsHome tools={homeTools} />;
};

export default App;
